import { useMemo, useState } from "react";

import { Button, Dialog } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import type { ModifierGroup } from "@/repositories/modifiersRepository";

import type { BasketProduct, ChosenModifier } from "./useBasket";

/**
 * "How would you like it?"
 *
 * Opens only when a product has questions to ask; everything else goes straight
 * into the basket on one tap, because a takeaway counter also sells cooldrinks
 * and a dialog in front of every Coke would be intolerable.
 *
 * Built for the same hands as the kitchen screen — large targets, no free text,
 * and the running total in the button so the cashier can read the price back to
 * the customer without doing arithmetic.
 *
 * Required groups start with their default selected. A cashier who has to
 * choose "regular" on every single burger will stop using modifiers and start
 * keying the difference in as a discount, which is worse than not having them.
 */
export function ModifierDialog({
  product,
  groups,
  onClose,
  onConfirm,
}: {
  product: BasketProduct;
  groups: ModifierGroup[];
  onClose: () => void;
  onConfirm: (modifiers: ChosenModifier[]) => void;
}) {
  const [chosen, setChosen] = useState<Record<string, string[]>>(() => defaultsFor(groups));

  const selected = useMemo(
    () =>
      groups.flatMap((group) =>
        group.modifiers.filter((modifier) => (chosen[group.id] ?? []).includes(modifier.id)),
      ),
    [groups, chosen],
  );

  const total =
    Number(product.selling_price) +
    selected.reduce((sum, modifier) => sum + Number(modifier.price_delta), 0);

  // A required group with nothing chosen is what would send the kitchen a ticket
  // it cannot make, so the button stays disabled and names what is missing.
  const missing = groups.filter(
    (group) => group.is_required && (chosen[group.id] ?? []).length < group.minimum,
  );

  function toggle(group: ModifierGroup, modifierId: string) {
    setChosen((current) => {
      const picked = current[group.id] ?? [];

      if (!group.allows_many) {
        // A single choice: tapping the chosen one again clears it only when the
        // group is optional. On a required group that would leave nothing set.
        const next = picked.includes(modifierId) && !group.is_required ? [] : [modifierId];
        return { ...current, [group.id]: next };
      }

      if (picked.includes(modifierId)) {
        return { ...current, [group.id]: picked.filter((id) => id !== modifierId) };
      }
      if (group.maximum && picked.length >= group.maximum) {
        return current;
      }
      return { ...current, [group.id]: [...picked, modifierId] };
    });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={product.name}
      description={missing.length > 0 ? `Choose ${missing[0]!.name.toLowerCase()}.` : "Anything extra?"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={missing.length > 0}
            onClick={() =>
              onConfirm(
                selected.map((modifier) => ({
                  id: modifier.id,
                  name: modifier.name,
                  price_delta: modifier.price_delta,
                })),
              )
            }
          >
            Add · {formatMoney(total.toFixed(2))}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {groups.map((group) => (
          <fieldset key={group.id}>
            <legend className="mb-1.5 text-sm font-medium text-ink-900">
              {group.name}
              {group.is_required && <span className="ml-1 text-danger-700">*</span>}
              {group.allows_many && group.maximum > 0 && (
                <span className="ml-1.5 text-xs font-normal text-ink-500">
                  up to {group.maximum}
                </span>
              )}
            </legend>

            <div className="flex flex-wrap gap-2">
              {group.modifiers
                .filter((modifier) => modifier.is_active)
                .map((modifier) => {
                  const picked = (chosen[group.id] ?? []).includes(modifier.id);
                  const delta = Number(modifier.price_delta);

                  return (
                    <button
                      key={modifier.id}
                      type="button"
                      aria-pressed={picked}
                      onClick={() => toggle(group, modifier.id)}
                      className={cn(
                        "min-h-11 rounded-control border px-3 py-2 text-left text-sm transition-colors",
                        picked
                          ? "border-brand-500 bg-brand-50 text-ink-900"
                          : "border-line bg-surface text-ink-600 hover:border-brand-500/40",
                      )}
                    >
                      <span className="block font-medium">{modifier.name}</span>
                      {delta !== 0 && (
                        <span className="block text-xs tabular-nums text-ink-500">
                          {delta > 0 ? "+" : "−"}
                          {formatMoney(Math.abs(delta).toFixed(2))}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          </fieldset>
        ))}
      </div>
    </Dialog>
  );
}

/**
 * What is selected when the dialog opens.
 *
 * A group's own default, or — for a required single choice with no default — its
 * first option. A required question that opens with nothing chosen makes a
 * cashier tap twice for the usual case, every time, all day.
 */
function defaultsFor(groups: ModifierGroup[]): Record<string, string[]> {
  const chosen: Record<string, string[]> = {};

  for (const group of groups) {
    const defaults = group.modifiers.filter((modifier) => modifier.is_default && modifier.is_active);
    if (defaults.length > 0) {
      chosen[group.id] = defaults.slice(0, group.maximum || defaults.length).map((m) => m.id);
    } else if (group.is_required && group.modifiers.length > 0) {
      chosen[group.id] = [group.modifiers[0]!.id];
    }
  }

  return chosen;
}
